import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, money } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import IdleGuard from "@/components/IdleGuard";
import { toast } from "sonner";
import { LogOut, Plus, ChevronRight, Receipt } from "lucide-react";

export default function CounterHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [services, setServices] = useState([]);
  const [funds, setFunds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [service, setService] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [chosenFunds, setChosenFunds] = useState([]);

  const load = () => {
    api.get("/sessions").then(({ data }) => setSessions(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/funds").then(({ data }) => setFunds(data));
  };
  useEffect(load, []);

  const active = sessions.filter((s) => !s.locked);
  const signed = sessions.filter((s) => s.locked);

  const createSession = async () => {
    const device_id = localStorage.getItem("patria_device");
    try {
      const { data } = await api.post("/sessions", { device_id, service, date, funds: chosenFunds });
      nav(`/count/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleFund = (name) =>
    setChosenFunds((f) => (f.includes(name) ? f.filter((x) => x !== name) : [...f, name]));

  return (
    <IdleGuard username={user.username}>
      <div className="min-h-screen pad-safe">
        <header className="flex items-center justify-between p-5 border-b border-[color:var(--line)] bg-white/70 backdrop-blur">
          <Wordmark sub={false} />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold">{user.name}</div>
              <div className="text-xs text-[color:var(--muted)]">Counter</div>
            </div>
            <button className="btn btn-ghost h-10 px-4" onClick={() => { logout(); nav("/"); }} data-testid="logout"><LogOut size={16} /></button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto p-5 space-y-6">
          {active.length > 0 && (
            <section>
              <h2 className="label mb-2">Active Session</h2>
              {active.map((s) => (
                <button key={s.id} className="card w-full p-5 flex items-center justify-between mb-3 active:scale-[0.99] transition-transform" onClick={() => nav(`/count/${s.id}`)} data-testid={`resume-${s.id}`}>
                  <div className="text-left">
                    <div className="font-semibold">{s.service} · {s.date}</div>
                    <div className="text-sm text-[color:var(--olive)]">{s.state}</div>
                  </div>
                  <ChevronRight className="text-[color:var(--muted)]" />
                </button>
              ))}
            </section>
          )}

          <section className="card p-6">
            {!creating ? (
              <button className="btn btn-primary w-full" onClick={() => setCreating(true)} data-testid="new-count"><Plus size={18} /> Start New Count</button>
            ) : (
              <div className="space-y-4">
                <h2 className="font-serif text-2xl">New Count Session</h2>
                <label className="block">
                  <span className="label block mb-1.5">Service</span>
                  <select className="field" value={service} onChange={(e) => setService(e.target.value)} data-testid="service-select">
                    <option value="">Select service…</option>
                    {services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="label block mb-1.5">Date</span>
                  <input className="field" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" />
                </label>
                <div>
                  <span className="label block mb-2">Fund Designations (optional)</span>
                  <div className="flex flex-wrap gap-2">
                    {funds.map((f) => (
                      <button key={f.id} onClick={() => toggleFund(f.name)} data-testid={`fund-${f.name}`}
                        className={`px-4 h-11 rounded-full border text-sm font-medium ${chosenFunds.includes(f.name) ? "bg-[color:var(--olive)] text-white border-[color:var(--olive)]" : "bg-white border-[color:var(--line)] text-[color:var(--ink-2)]"}`}>
                        {f.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button className="btn btn-ghost flex-1" onClick={() => setCreating(false)}>Cancel</button>
                  <button className="btn btn-primary flex-1" disabled={!service} onClick={createSession} data-testid="create-session">Open Session</button>
                </div>
              </div>
            )}
          </section>

          <section>
            <h2 className="label mb-2 flex items-center gap-1"><Receipt size={13} /> My Signed Receipts</h2>
            {signed.length === 0 && <p className="text-sm text-[color:var(--muted)]">No completed counts yet.</p>}
            <div className="space-y-2">
              {signed.map((s) => (
                <div key={s.id} className="card p-4 flex items-center justify-between" data-testid={`receipt-${s.id}`}>
                  <div>
                    <div className="font-semibold">{s.service} · {s.date}</div>
                    <div className="text-xs text-[color:var(--muted)]">Locked</div>
                  </div>
                  <div className="font-mono tabnum font-semibold text-[color:var(--olive)]">{money(s.computed_totals?.grand_total)}</div>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    </IdleGuard>
  );
}
